import { IEntityContentJson } from '@mailupinc/bee-plugin/dist/types/bee';
import { BeeConfig } from './types';

/**
 * These are label texts for the Drag and Drop editor
 */
export const CONTENT = {
  addCustomFontCta: 'Add custom font',
  manageCustomFontsCta: 'Manage custom fonts',
};

/**
 * Note: the `BEE_PLUGIN_CONTAINER_ID` will be assigned to the id of an outer div containing the editor. The id will
 * also be assigned to the id attribute (undocumented) of the iframe containing the editor with an
 * "__bee-plugin-frame" suffix (example: 'beePluginContainer___bee-plugin-frame').
 *
 * For testing: Within that iframe, the actual working content is located in a nested div with class
 * 'stageContent'
 */
export const BEE_PLUGIN_CONTAINER_ID = 'beePluginContainer';

export const SPECIAL_LINKS = [
  {
    type: 'Special links',
    label: 'Click here to unsubscribe.',
    link: '{{unsubscribeUrl}}',
  },
  {
    type: 'Special links',
    label: 'Click here to view this email in your browser.',
    link: '{{viewInBrowserUrl}}',
  },
];

export const HANDLEBARS_HELPERS = [
  {
    name: '@first',
    value: '@first',
  },
  {
    name: '@last',
    value: '@last',
  },
  {
    name: '@key',
    value: '@key',
  },
  {
    name: '@index',
    value: '@index',
  },
];

/**
 * Default BEE configuration object
 * @see https://docs.beefree.io/configuration-parameters/
 */
export const DEFAULT_BEE_PLUGIN_CONFIG: BeeConfig = {
  // autosave: 5, // default: false
  container: BEE_PLUGIN_CONTAINER_ID,
  /**
   * Defines the order of the contents
   * @see https://docs.beefree.io/content-tile-sorting/
   */
  defaultModulesOrder: [
    'Heading',
    'Text',
    'Button',
    'Paragraph',
    'List',
    'Menu',
    'Divider',
    'Spacer',
    'Html',
    'Image',
    'Video',
    'Social',
    'Icons',
  ],
  /**
   * `BeeConfig` types does not support `disableLinkSanitize` yet
   */
  // @ts-ignore
  disableLinkSanitize: true,
  editorFonts: {
    customFonts: [],
    showDefaultFonts: true,
  },
  specialLinks: SPECIAL_LINKS,
  trackChanges: true,
  /**
   * Disables the loading spinner for content dialogs
   * @see https://iterable.slack.com/archives/C03052SB1FC/p1718990480361009?thread_ts=1718738519.134309&cid=C03052SB1FC
   */
  loadingSpinnerDisableOnDialog: true,
};

export const DYNAMIC_CONTENT_BUILDER_ADDON_HANDLE = 'dynamicContentBuilder';
export const MOVABLE_INK_ADDON_HANDLE = 'movableInk';

export const DEFAULT_SAVED_ROWS_CATEGORY_BEHAVIORS = {
  canEdit: true,
  canDelete: true,
  canEditSyncedRows: false,
  canDeleteSyncedRows: false,
};

export const DEFAULT_SAVED_ROWS_CATEGORY_VALUES = {
  isLocal: true,
  behaviors: DEFAULT_SAVED_ROWS_CATEGORY_BEHAVIORS,
};

export const MY_SAVED_ROWS_CATEGORY = {
  name: 'My saved rows',
  value: 'category-value-my-saved-rows',
  handle: 'category-handle-my-saved-rows',
  ...DEFAULT_SAVED_ROWS_CATEGORY_VALUES,
};

export const ALL_SAVED_ROWS_CATEGORY = {
  name: 'All saved rows',
  value: 'category-value-all-saved-rows',
  handle: 'category-handle-all-saved-rows',
  ...DEFAULT_SAVED_ROWS_CATEGORY_VALUES,
};

export const PRESET_SAVED_ROWS_CATEGORIES = [MY_SAVED_ROWS_CATEGORY, ALL_SAVED_ROWS_CATEGORY];

export const MANAGE_CUSTOM_CATEGORIES_ID = {
  header: 'manage-custom-categories-header-id',
  modal: 'manage-custom-categories-modal-id',
};

export const CREATE_SAVED_ROW_ERROR_MESSAGE = 'Unable to save saved row. Please try again.';
export const DELETE_SAVED_ROW_ERROR_MESSAGE = 'Unable to delete saved row. Please try again.';

export const CATEGORY_LIMIT = 30;
export const CATEGORY_LIMIT_TOOLTIP = 'Limit of 30 categories';
export const CATEGORY_LIMIT_ERROR =
  'Limit of 30 categories reached. Please select or delete a category to create a new one.';

export const SAVED_ROW_LIMIT = 500;
export const SAVED_ROW_LIMIT_ERROR = 'Max of 500 rows per project. Delete rows to save a new row.';

export const QUERY_BEE_TOKEN_ERROR_MESSAGE = 'Unable fetch auth token';
export const QUERY_MERGE_PARAMS_ERROR_MESSAGE = 'Unable to fetch merge params';
export const QUERY_MESSAGE_CATEGORIES_ERROR_MESSAGE = 'Unable to fetch saved rows categories';
export const UPDATE_SAVED_ROW_ERROR_MESSAGE = 'Unable to update saved row. Please try again.';

export const CREATE_SAVED_ROW_SUCCESS_MESSAGE = 'saved row successfully created.';
export const UPDATE_SAVED_ROW_SUCCESS_MESSAGE = 'saved row successfully updated.';
export const DELETE_SAVED_ROW_SUCCESS_MESSAGE = 'saved row successfully deleted.';

/**
 * Default saved row page container configuration.
 */
export const DEFAULT_SAVED_ROW_PAGE_CONTAINER_CONFIG = {
  page: {
    body: {
      container: {
        style: {
          'background-color': '#FFFFFF',
        },
      },
      content: {
        computedStyle: {
          linkColor: '#1871D8',
          messageBackgroundColor: '#FFFFFF',
          messageWidth: '500px',
        },
        style: {
          color: '#000000',
          'font-family': 'Arial, Helvetica Neue, Helvetica, sans-serif',
        },
      },
      webFonts: [],
    },
    description: '',
    rows: [],
    template: {
      version: '2.0.0',
    },
    title: '',
    language: {
      label: 'English',
      value: 'en-US',
    },
  },
  comments: {},
} as unknown as IEntityContentJson;
